<template>
  <div
    :key="Math.random()"
    :class="[isBigKpi ? ' flex flex-col min-w-full w-full overflow-y-auto overflow-x-hidden min-h-full h-full' : 'min-h-[75px] h-full']"
    :style="{ backgroundColor: backgroundColor }"
    class="relative rounded-md shadow-md bg-light-box-background dark:bg-dark-box-background transition-ease-in-out duration-side-menu-transition-duration">
    <div class="font-bold pl-[16px] pb-1 pt-1 flex justify-between">
      <div v-if="metric?.input_type" class="flex items-center justify-center">
        <div class="mr-1 -ml-1">
          <NumbersIcon v-if="metric?.input_type == 'Number'" class="w-[48px] h-[48px]" />
          <DecimalIcon v-else-if="metric?.input_type == 'Decimal'" class="w-[48px] h-[48px]" />
          <PercentIcon v-else-if="metric?.input_type == 'Percent'" class="w-[48px] h-[48px]" />
          <CurrencyIcon v-else-if="metric?.input_type == 'Currency'" class="w-[48px] h-[48px]" />
          <StarRatingIcon v-else-if="metric?.input_type == 'StarRating'" class="w-[48px] h-[48px]" />
          <BarRatingIcon v-else-if="metric?.input_type == 'BarRating'" class="w-[48px] h-[48px]" />
        </div>
        <div class="">
          {{ name }}
        </div>
      </div>
      <div v-else :style="{ color: textColor }" class="dark:text-dark-body-text">
        {{ name }}
        <span
          v-if="pageReference && pageReference == PAGE_REFERENCES.DASHBOARD"
          :style="{ color: textColor }"
          class="text-[11px] text-light text-common-grey flex mt-0.5 capitalize whitespace-nowrap">{{ graph.axis?.object_type }}</span>
      </div>
      <Menu v-if="!editing_disabled" as="div" class="flex-shrink-0 ml-auto">
        <MenuButton
          :style="{ backgroundColor: backgroundColor }"
          class="inline-flex items-center justify-center text-gray-400 transition ease-in-out bg-white rounded dark:bg-dark-box-background duration-side-menu-transition-duration"
          @click.stop="">
          <span class="sr-only">{{ $t("Open options") }}</span>
          <EllipsisVertical
            v-if="isEditable"
            :color="$store.state.ui.darkMode ? '#fff' : textColor"
            :style="{ color: textColor }"
            class="w-5 h-5 border-none" />
        </MenuButton>
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0">
          <MenuItems
            :class="[isBigKpi && hasCheckbox ? 'h-fit' : '']"
            class="absolute top-0 right-0 z-30 flex flex-col items-start content-center justify-center p-2 mx-3 mt-1 origin-top-right bg-white rounded-md shadow-lg outline-none dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none">
            <MenuItem
              v-slot="{ active }"
              @click.prevent="
                () => {
                  editChartKPICallback && editChartKPICallback()
                  showMenu = !showMenu
                  if (!isBigKpi) {
                    $emitter.emit(ALL_EMITTERS.EDIT_GRAPH, {
                      graph: graph,
                      page_reference: pageReference
                    })
                    $emitter.emit(ALL_EMITTERS.EDIT_GRAPH_IN_GRAPHS_GRID, {
                      graph: graph,
                      page_reference: pageReference
                    })
                  }
                }
              ">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <Pencil :class="hasCheckbox && 'translate-y-[-7px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="[hasCheckbox && 'translate-y-[-7px]']">{{ isBigKpi ? "Edit Target" : "Edit Graph" }}</span>
              </div>
            </MenuItem>
            <MenuItem
              v-if="isBigKpi"
              v-slot="{ active }"
              @click.prevent="
                () => {
                  editChartKPICallback && editChartKPICallback('range-target')
                  showMenu = !showMenu
                }
              ">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <Pencil :class="hasCheckbox && 'translate-y-[-7px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="[hasCheckbox && 'translate-y-[-7px]']">{{ $t("Set current range target") }}</span>
              </div>
            </MenuItem>
            <MenuItem v-if="!isBigKpi" v-slot="{ active }" @click.prevent="duplicateChart(graph)">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <Copy :class="hasCheckbox && 'translate-y-[-7px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="[hasCheckbox && 'translate-y-[-7px]']">{{ $t("Duplicate Graph") }}</span>
              </div>
            </MenuItem>
            <MenuItem
              v-show="graph.graph_type != 'table' && graph.graph_type != 'treemap' && graph.graph_type != 'radialBar'"
              v-if="!isBigKpi"
              v-slot="{ active }"
              @click.prevent="graph && showHideLabels()">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <ChartNoAxesColumnDecreasing :class="hasCheckbox && 'translate-y-[-5px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="hasCheckbox && 'translate-y-[-7px]'">{{ graph.graph_options?.show_labels == false ? $t("Show labels") : $t("Hide labels") }}
                </span>
              </div>
            </MenuItem>
            <MenuItem
              v-show="graph.graph_type != 'table' && graph.graph_type != 'treemap' && graph.graph_type != 'radialBar'"
              v-if="!isBigKpi"
              v-slot="{ active }"
              @click.prevent="graph && showHideValues()">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <FileDigit :class="hasCheckbox && 'translate-y-[-5px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="hasCheckbox && 'translate-y-[-7px]'">{{
                  graph.graph_options?.show_values == false ? $t("Show values") : $t("Hide values")
                }}</span>
              </div>
            </MenuItem>

            <MenuItem
              v-show="graph.graph_type != 'table' && graph.graph_type != 'treemap' && graph.graph_type != 'radialBar'"
              v-if="!isBigKpi"
              v-slot="{ active }"
              @click.prevent="graph && showHideDataLabels()">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <ChartNoAxesColumnDecreasing :class="hasCheckbox && 'translate-y-[-5px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="hasCheckbox && 'translate-y-[-7px]'">{{ !graph.graph_options?.show_data_labels ? $t("Show data labels") : $t("Hide data labels") }}
                </span>
              </div>
            </MenuItem>

            <MenuItem
              v-show="
                graph.graph_type != 'horizontal_bars' &&
                  graph.graph_type != 'stacked_horizontal_bars' &&
                  graph.graph_type != 'table' &&
                  graph.graph_type != 'pie' &&
                  graph.graph_type != 'polarArea' &&
                  graph.graph_type != 'heatmap' &&
                  graph.graph_type != 'radialBar' &&
                  graph.graph_type != 'radar'
              "
              v-if="!isBigKpi"
              v-slot="{ active }"
              @click.prevent="graph && showHideZoom()">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <ScanSearch :class="hasCheckbox && 'translate-y-[-5px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="hasCheckbox && 'translate-y-[-7px]'">{{
                  graph.graph_options?.show_zoom == false ? $t("Show zoom") : $t("Hide zoom")
                }}</span>
              </div>
            </MenuItem>
            <MenuItem v-if="!isBigKpi" v-slot="{ active }" @click.prevent="graph && $emitter.emit(ALL_EMITTERS.DELETE_GRAPH, graph)">
              <div
                :class="[
                  active ? 'text-gray-900 dark:text-dark-body-text' : 'text-gray-600 dark:text-dark-body-text',
                  'group flex items-center px-3 py-2 text-sm cursor-pointer'
                ]">
                <Trash2 :class="hasCheckbox && 'translate-y-[-5px]'" class="w-5 h-5 mr-4" aria-hidden="true" />
                <span :class="hasCheckbox && 'translate-y-[-7px]'">{{ $t("Delete Graph") }}</span>
              </div>
            </MenuItem>
          </MenuItems>
        </transition>
      </Menu>
    </div>
    <div :class="[isBigKpi ? ' m-0' : 'self-center w-full']">
      <slot />
    </div>
  </div>
</template>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { EllipsisVertical } from "lucide-vue-next"
import { ScanSearch, FileDigit, Trash2, Pencil, Copy, ChartNoAxesColumnDecreasing } from "lucide-vue-next"
import NumbersIcon from "@/components/shared/icons/NumbersIcon.vue"
import DecimalIcon from "@/components/shared/icons/DecimalIcon.vue"
import PercentIcon from "@/components/shared/icons/PercentIcon.vue"
import CurrencyIcon from "@/components/shared/icons/CurrencyIcon.vue"
import StarRatingIcon from "@/components/shared/icons/StarRatingIcon.vue"
import BarRatingIcon from "@/components/shared/icons/BarRatingIcon.vue"
import { PAGE_REFERENCES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    NumbersIcon,
    DecimalIcon,
    PercentIcon,
    CurrencyIcon,
    StarRatingIcon,
    BarRatingIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ChartNoAxesColumnDecreasing,
    FileDigit,
    ScanSearch,
    EllipsisVertical,
    Pencil,
    Trash2,
    Copy
  },

  inject: ["canUserEditLayout"],
  props: {
    backgroundColor: null,
    textColor: null,
    dataSource: null,
    isBigKpi: {
      type: Boolean,
      default: null
    },
    metric: {
      type: Object,
      required: false
    },
    name: {
      type: String,
      default: "Table Name"
    },
    graph: {
      type: Object,
      default: null
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    hasCheckbox: {
      type: Boolean,
      default: false
    },
    editChartKPICallback: {
      type: Function,
      default: null
    },
    pageReference: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      ALL_EMITTERS,
      showMenu: false,
      PAGE_REFERENCES
    }
  },

  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  methods: {
    duplicateChart(graph) {
      let graph_clone = utils.clone(graph)
      delete graph_clone["id"]
      delete graph_clone["user"]
      delete graph_clone["datasets"]
      let new_name = ""
      if (!graph_clone["name"].includes("(Copy#")) {
        new_name = graph_clone["name"] + " (Copy#1)"
      } else {
        new_name = graph_clone["name"].replace(/\(Copy#(\d+)\)/g, (match, g1) => `(Copy#${Number(g1) + 1})`)
      }
      graph_clone["name"] = new_name
      graph_clone["axis"] = graph_clone["axis"]?.id
      graph_clone["series"] = graph_clone["series"].map((s) => {
        delete s["id"]
        s["metric"] = s["metric"].id
        return s
      })

      this.$store.dispatch("data/createGraph", graph_clone).then((res) => {
        const response = res
        if (this.dataSource) {
          response["dataSource"] = this.dataSource
        }
        if (this.textColor) {
          response["textColor"] = this.textColor
        }
        if (this.backgroundColor) {
          response["backgroundColor"] = this.backgroundColor
        }

        this.$emitter.emit(ALL_EMITTERS.ADD_GRAPH_TO_LAYOUT, response)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_CREATED, response)
        this.$toast.open({
          message: this.$t("Graph") + ` ${graph.name} ` + this.$t("duplicated successfully"),
          position: "top",
          type: "success",
          duration: 3000
        })
      })
    },
    showHideLabels() {
      let data = this.$utils.clone({
        id: this.graph.id,
        graph_options: this.graph.graph_options
      })
      if (data.graph_options?.show_labels == false) {
        data.graph_options["show_labels"] = true
      } else {
        data.graph_options["show_labels"] = false
      }
      this.$store.dispatch("data/updateGraph", { id: this.graph.id, data }).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_ADD_NEW_COMPONENTS_MODAL, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_GRAPHS_GRID, res)
      })
    },

    showHideDataLabels() {
      let data = this.$utils.clone({
        id: this.graph.id,
        graph_options: this.graph.graph_options
      })
      if (!data.graph_options?.show_data_labels) {
        data.graph_options["show_data_labels"] = true
      } else {
        data.graph_options["show_data_labels"] = false
      }
      this.$store.dispatch("data/updateGraph", { id: this.graph.id, data }).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_ADD_NEW_COMPONENTS_MODAL, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_GRAPHS_GRID, res)
      })
    },
    showHideValues() {
      let data = this.$utils.clone({
        id: this.graph.id,
        graph_options: this.graph.graph_options
      })
      if (data.graph_options?.show_values == false) {
        data.graph_options["show_values"] = true
      } else {
        data.graph_options["show_values"] = false
      }
      this.$store.dispatch("data/updateGraph", { id: this.graph.id, data }).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_ADD_NEW_COMPONENTS_MODAL, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_GRAPHS_GRID, res)
      })
    },
    showHideZoom() {
      let data = this.$utils.clone({
        id: this.graph.id,
        graph_options: this.graph.graph_options
      })
      if (data.graph_options?.show_zoom == false) {
        data.graph_options["show_zoom"] = true
      } else {
        data.graph_options["show_zoom"] = false
      }
      this.$store.dispatch("data/updateGraph", { id: this.graph.id, data }).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_ADD_NEW_COMPONENTS_MODAL, res)
        this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_GRAPHS_GRID, res)
      })
    }
  }
}
</script>
